import { useCallback, useEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { PopupProps } from './Modal.types';
import ModalHeader from './ModalHeader';
import ModalBody from './ModalBody';
import ModalFooter from './ModalFooter';
import ModalFooterGeneric from './ModalFooterGeneric';
import { StyledPopUpContentContainerDiv, StyledPopupDiv } from './Modal.styles';
import clsx from 'clsx';
import { isIOSDevice } from '../../utils/helper';
import { usePortalStyleContext } from '../../context/PortalStyleContext';

const Modal: React.FC<PopupProps> = ({
  className,
  show,
  children,
  handleBlur,
  popupOpenState,
  setPopupOpenState,
  role,
  labelledBy,
  onClose,
  secondaryClassName,
  eventClass = '',
  modalId,
  disableScrollOnBody = false,
  flexLayout = false,
  usePortal = false,
  portalContainer = null,
  isTooltipPopup = true,
  ...props
}) => {
  /**
   * convert's string to array of classes by removing the empty space's & empty string
   */
  const targetClass = useMemo(() => eventClass?.split(' ')?.filter(cls => cls?.trim() !== ''), [eventClass]);

  const popupRef = useRef<HTMLDivElement>(null);

  // Keep all close side effects in one place so each close trigger updates state consistently once.
  const closeModal = useCallback(() => {
    setPopupOpenState?.(false);
    // If both props point to the same callback, avoid invoking it twice.
    if (onClose && onClose !== setPopupOpenState) {
      onClose();
    }
  }, [onClose, setPopupOpenState]);

  const { portalClassName } = usePortalStyleContext();

  useEffect(() => {
    const popupElem = popupRef.current;
    if (popupElem) {
      // Attach a single keydown listener for focus trap + Escape close to avoid duplicate onClose calls.
      const handleKeyDown = (event: KeyboardEvent) => {
        // Find all focusable elements within the popup
        const focusableElements = popupElem?.querySelectorAll(
          '[tabindex]:not([disabled]), a[href]:not([disabled]), button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled])'
        );
        // Find the first and last focusable elements
        const firstFocusableElement = focusableElements?.[0] as HTMLInputElement;
        const lastFocusableElement = focusableElements?.[focusableElements.length - 1] as HTMLInputElement;
        // Handle keyboard events
        if (event.key === 'Tab') {
          if (!event.shiftKey) {
            // Tab pressed (forward)
            if (document.activeElement === lastFocusableElement) {
              event.preventDefault(); // Prevent default tab behavior
              firstFocusableElement?.focus(); // Focus on the first element
            }
          } else if (event.shiftKey) {
            // Shift+Tab pressed (backward)
            if (document.activeElement === firstFocusableElement) {
              event.preventDefault(); // Prevent default tab behavior
              lastFocusableElement?.focus(); // Focus on the last element
            }
          }
        } else if (event.key === 'Escape') {
          closeModal();
        }
      };

      // Add event listener for keyboard navigation
      popupElem.addEventListener('keydown', handleKeyDown);

      return () => {
        popupElem.removeEventListener('keydown', handleKeyDown);
      };
    } else return;
  }, [closeModal]);

  useEffect(() => {
    // close popup on desktop if tab focus is out of popup or user clicks outside the popup
    if (handleBlur && popupOpenState && setPopupOpenState) {
      const popupElem = popupRef.current;
      if (popupElem && popupOpenState) {
        // Below, we are checking if the clicking is on any of the elements that are not contained
        // within 'popupElem' but are used to either open the popup or to return to the popup and closing popup accordingly
        const clickListener = (event: Event): void => {
          if (
            !popupElem.contains(event.target as Element) &&
            !popupElem.contains(document.activeElement) &&
            !(
              document.activeElement?.classList.contains('sm-search-result-all-filter-component-button') ||
              document.activeElement?.classList.contains('hqv-modal-opener') ||
              // The 'gm-ui-hover-effect' is the class contained in the google maps 'keyboard controls' close button.
              // Adding the condition prevents unexpected modal closings by clicking on the close btn.
              document.activeElement?.classList.contains('gm-ui-hover-effect') ||
              (event?.target as HTMLElement)?.parentElement?.classList.contains('return-to-categories') ||
              (event?.target as HTMLElement)?.classList.contains('return-to-categories') ||
              (event?.target as HTMLElement)?.classList.contains('gm-ui-hover-effect') ||
              targetClass?.some(cls => (event?.target as HTMLElement)?.classList?.contains(cls))
            )
          ) {
            closeModal();
          }
        };

        // focus out handler
        const focusoutListener = (event: FocusEvent): void => {
          if (
            event.relatedTarget &&
            !popupElem.contains(event.relatedTarget as Element) &&
            !popupElem.contains(document.activeElement) &&
            !popupElem.contains(event.target as Element)
          ) {
            closeModal();
          }
        };
        const clickListenerForToolTip = (event: Event): void => {
          if (
            !popupElem.contains(event.target as Element) &&
            !popupElem.contains(document.activeElement) &&
            !(event.target as Element)?.classList.contains('m-link-icon-button-info')
          ) {
            closeModal();
          }
        };
        //Only if its a tooltip popup
        if (secondaryClassName && isTooltipPopup) {
          document.addEventListener('click', clickListenerForToolTip);
        } //other popups
        else {
          // Register exactly one click/focusout pair for standard modals.
          document.addEventListener('click', clickListener);
          popupElem.addEventListener('focusout', focusoutListener);
        }

        return (): void => {
          document.removeEventListener('click', clickListener);
          document.removeEventListener('click', clickListenerForToolTip);
          popupElem.removeEventListener('focusout', focusoutListener);
        };
      }
    }

    // eslint-disable-next-line @typescript-eslint/no-empty-function
    return (): void => {};
  }, [closeModal, handleBlur, isTooltipPopup, popupOpenState, secondaryClassName, setPopupOpenState, targetClass]);

  useEffect(() => {
    if (popupOpenState) {
      popupRef?.current?.focus();
    }
  }, [popupOpenState]);

  useEffect(() => {
    if (!disableScrollOnBody) return;
    document.body.style.overflow = show ? 'hidden' : 'auto';

    // Applying disable scroll when pop up is open and the below CSS added as part of iOS devices fix

    // On iOS, avoid using position: fixed (breaks sticky). Instead, block background scroll via touch/wheel handlers.
    let preventBackgroundScroll: ((event: Event) => void) | undefined;

    if (popupOpenState) {
      const popupElem = popupRef.current;
      preventBackgroundScroll = (event: Event) => {
        if (!popupElem) return;
        const target = event.target as Node | null;
        if (!target || !popupElem.contains(target)) {
          event.preventDefault();
        }
      };
      ['touchmove', 'wheel'].forEach(type => {
        document.addEventListener(type, preventBackgroundScroll as EventListener, { passive: false });
      });
      if (isIOSDevice()) {
        document.body.dataset['iOSScrollLock'] = 'true';
      } else {
        document.body.dataset['ScrollLock'] = 'true';
      }
    }

    //return to unmount all styles
    return () => {
      document.body.style.overflow = 'auto';
      if (document.body.dataset['ScrollLock'] || document.body.dataset['iOSScrollLock']) {
        ['touchmove', 'wheel'].forEach(type => {
          if (preventBackgroundScroll)
            document.removeEventListener(
              type,
              preventBackgroundScroll as EventListener,
              { capture: false } as EventListenerOptions
            );
        });
        if (isIOSDevice()) {
          delete document.body.dataset['iOSScrollLock'];
        } else {
          delete document.body.dataset['ScrollLock'];
        }
      }
    };
  }, [show, disableScrollOnBody, popupOpenState]);

  const modalContent = (
    <StyledPopupDiv
      data-component-name="m-ui-library-Modal"
      data-testid="ui-library-Modal"
      className={clsx(className, usePortal && portalClassName)}
      show={show}
    >
      <StyledPopUpContentContainerDiv
        className={clsx('custom-scrollbar', secondaryClassName)}
        ref={popupRef}
        role={role}
        aria-labelledby={labelledBy}
        tabIndex={0}
        flexLayout={flexLayout}
        {...props}
        {...(modalId ? { id: modalId } : {})} // add id props
      >
        {children}
      </StyledPopUpContentContainerDiv>
    </StyledPopupDiv>
  );

  // Use portal to render modal at document.body level, escaping parent stacking contexts
  if (usePortal && typeof document !== 'undefined') {
    const portalTarget = portalContainer ?? document.body;
    return createPortal(modalContent, portalTarget);
  }

  return modalContent;
};

export default Object.assign(Modal, {
  Header: ModalHeader,
  Body: ModalBody,
  Footer: ModalFooter,
  FooterGeneric: ModalFooterGeneric,
});
